import { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Button, Input } from '@lms/ui';
import type { Program } from '@lms/shared/types';
import { useUpdateProgram } from '../hooks/usePrograms';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from './Bilingual';
import { ProgramCoverField } from './ProgramCoverField';
import { copy } from '../copy/copy';

interface ProgramModalProps {
  program: Program;
  onClose: () => void;
}

const formNavigation = [
  { target: 'program-identity', label: copy.programModal.sectionIdentity },
  { target: 'program-appearance', label: copy.programModal.sectionAppearance },
  { target: 'program-access', label: copy.programModal.sectionAccess },
  { target: 'program-schedule', label: copy.programModal.sectionSchedule },
] as const;

function toStartOfDay(value: string): Date {
  return new Date(`${value}T00:00:00`);
}

function toEndOfDay(value: string): Date {
  return new Date(`${value}T23:59:59.999`);
}

export function ProgramModal({ program, onClose }: ProgramModalProps) {
  const { mutate: update, isPending } = useUpdateProgram();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: program.name,
    code: program.code,
    description: program.description || '',
    coverImage: program.coverImage || '',
    status: program.status,
    approvalMode: program.approvalMode,
    isRegistrationOpen: program.isRegistrationOpen,
    registrationOpensAt: program.registrationOpensAt
      ? new Date(program.registrationOpensAt).toISOString().split('T')[0]
      : '',
    registrationClosesAt: program.registrationClosesAt
      ? new Date(program.registrationClosesAt).toISOString().split('T')[0]
      : '',
    startDate: program.startDate ? new Date(program.startDate).toISOString().split('T')[0] : '',
    endDate: program.endDate ? new Date(program.endDate).toISOString().split('T')[0] : '',
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    update(
      {
        id: program.id,
        data: {
          ...formData,
          coverImage: formData.coverImage ?? '',
          registrationOpensAt: formData.registrationOpensAt
            ? toStartOfDay(formData.registrationOpensAt)
            : null,
          registrationClosesAt: formData.registrationClosesAt
            ? toEndOfDay(formData.registrationClosesAt)
            : null,
          startDate: formData.startDate ? toStartOfDay(formData.startDate) : undefined,
          endDate: formData.endDate ? toEndOfDay(formData.endDate) : undefined,
        },
      },
      { onSuccess: onClose }
    );
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormData(previous => ({ ...previous, [name]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/40 p-4 backdrop-blur-sm animate-fadeIn sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="program-modal-title"
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-[2rem] bg-surface-container-lowest shadow-level4 animate-slideUp sm:max-h-[calc(100dvh-3rem)]"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 bg-surface-container-low px-5 py-5 sm:px-8 sm:py-6">
          <div>
            <h2
              id="program-modal-title"
              className="m-0 text-2xl font-headline font-bold tracking-tight text-on-surface"
            >
              <Bilingual {...copy.programModal.title} />
            </h2>
            <p className="mt-1 text-xs font-body font-bold uppercase tracking-widest text-on-surface-variant/60">
              <Bilingual {...copy.programModal.subtitle} />
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t(copy.common.close)}
            className="flex size-10 shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <X size={20} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-5 py-6 sm:px-8 sm:py-8">
            <nav
              aria-label={t(copy.programModal.formNavigationLabel)}
              className="grid grid-cols-2 gap-1 rounded-xl bg-surface-container-low p-1 sm:grid-cols-4"
            >
              {formNavigation.map(({ target, label }) => (
                <a
                  key={target}
                  href={`#${target}`}
                  className="rounded-lg px-3 py-2 text-center text-xs font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {t(label)}
                </a>
              ))}
            </nav>

            <section
              id="program-identity"
              className="scroll-mt-4 space-y-5"
              aria-labelledby="program-identity-title"
            >
              <h3
                id="program-identity-title"
                className="text-sm font-headline font-bold text-on-surface"
              >
                <Bilingual {...copy.programModal.sectionIdentity} />
              </h3>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label
                    htmlFor="program-code"
                    className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant/60"
                  >
                    <Bilingual {...copy.programModal.labelCode} />
                  </label>
                  <Input
                    id="program-code"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    required
                    className="h-12 border-none bg-surface-container-low focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    htmlFor="program-name"
                    className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant/60"
                  >
                    <Bilingual {...copy.programModal.labelName} />
                  </label>
                  <Input
                    id="program-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 border-none bg-surface-container-low focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="program-description"
                  className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant/60"
                >
                  <Bilingual {...copy.programModal.labelDesc} />
                </label>
                <textarea
                  id="program-description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full resize-none rounded-xl bg-surface-container-low px-4 py-3 text-sm font-body text-on-surface outline-none transition-all placeholder:text-on-surface-variant/50 focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </section>

            <section
              id="program-appearance"
              className="scroll-mt-4"
              aria-labelledby="program-appearance-title"
            >
              <h3 id="program-appearance-title" className="sr-only">
                <Bilingual {...copy.programModal.sectionAppearance} />
              </h3>
              <ProgramCoverField
                id="program-cover-edit"
                value={formData.coverImage}
                programName={formData.name}
                onChange={coverImage => setFormData(previous => ({ ...previous, coverImage }))}
                label={<Bilingual {...copy.programModal.labelCoverImage} />}
                uploadPrompt={<Bilingual {...copy.programModal.uploadCoverPrompt} />}
                recommendation={<Bilingual {...copy.programModal.coverRecommendation} />}
                uploadingLabel={<Bilingual {...copy.programModal.uploadingCover} />}
                changeLabel={<Bilingual {...copy.programModal.changeCover} />}
                removeLabel={<Bilingual {...copy.programModal.removeCover} />}
                previewUnavailable={<Bilingual {...copy.programModal.coverPreviewUnavailable} />}
              />
            </section>

            <section
              id="program-access"
              className="scroll-mt-4 space-y-5"
              aria-labelledby="program-access-title"
            >
              <h3
                id="program-access-title"
                className="text-sm font-headline font-bold text-on-surface"
              >
                <Bilingual {...copy.programModal.sectionAccess} />
              </h3>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label
                    htmlFor="program-status"
                    className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant/60"
                  >
                    <Bilingual {...copy.programModal.labelStatus} />
                  </label>
                  <select
                    id="program-status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="h-12 w-full appearance-none rounded-xl bg-surface-container-low px-4 py-3 text-sm font-body text-on-surface outline-none transition-all focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="DRAFT">{t(copy.programModal.statusDraft)}</option>
                    <option value="PUBLISHED">{t(copy.programModal.statusPublished)}</option>
                    <option value="ARCHIVED">{t(copy.programModal.statusArchived)}</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label
                    htmlFor="program-admission"
                    className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant/60"
                  >
                    <Bilingual {...copy.programModal.labelAdmission} />
                  </label>
                  <select
                    id="program-admission"
                    name="approvalMode"
                    value={formData.approvalMode}
                    onChange={handleChange}
                    className="h-12 w-full appearance-none rounded-xl bg-surface-container-low px-4 py-3 text-sm font-body text-on-surface outline-none transition-all focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="MANUAL">{t(copy.programModal.admissionManual)}</option>
                    <option value="AUTO_APPROVE">{t(copy.programModal.admissionAuto)}</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-between gap-5 rounded-2xl bg-surface-container-low p-5">
                <div>
                  <p className="text-sm font-bold text-on-surface">
                    <Bilingual {...copy.programModal.labelRegistrationOpen} />
                  </p>
                  <p className="mt-0.5 text-[10px] font-black uppercase tracking-widest text-on-surface-variant/60">
                    <Bilingual {...copy.programModal.registrationOpenDesc} />
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFormData(previous => ({
                      ...previous,
                      isRegistrationOpen: !previous.isRegistrationOpen,
                    }))
                  }
                  aria-label={t(copy.programModal.labelRegistrationOpen)}
                  aria-pressed={formData.isRegistrationOpen}
                  className={`relative h-8 w-14 shrink-0 rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                    formData.isRegistrationOpen ? 'bg-primary' : 'bg-surface-container-high'
                  }`}
                >
                  <span
                    className={`absolute top-1 size-6 rounded-full bg-surface-container-lowest shadow-sm transition-all duration-300 ${
                      formData.isRegistrationOpen ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </section>

            <section
              id="program-schedule"
              className="scroll-mt-4 space-y-5"
              aria-labelledby="program-schedule-title"
            >
              <h3
                id="program-schedule-title"
                className="text-sm font-headline font-bold text-on-surface"
              >
                <Bilingual {...copy.programModal.sectionSchedule} />
              </h3>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label
                    htmlFor="program-registration-opens-at"
                    className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant/60"
                  >
                    <Bilingual {...copy.programModal.labelRegistrationOpenDate} />
                  </label>
                  <Input
                    id="program-registration-opens-at"
                    type="date"
                    name="registrationOpensAt"
                    value={formData.registrationOpensAt}
                    onChange={handleChange}
                    className="h-12 border-none bg-surface-container-low focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    htmlFor="program-registration-closes-at"
                    className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant/60"
                  >
                    <Bilingual {...copy.programModal.labelRegistrationCloseDate} />
                  </label>
                  <Input
                    id="program-registration-closes-at"
                    type="date"
                    name="registrationClosesAt"
                    value={formData.registrationClosesAt}
                    onChange={handleChange}
                    className="h-12 border-none bg-surface-container-low focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    htmlFor="program-start-date"
                    className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant/60"
                  >
                    <Bilingual {...copy.programModal.labelStartDate} />
                  </label>
                  <Input
                    id="program-start-date"
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="h-12 border-none bg-surface-container-low focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    htmlFor="program-end-date"
                    className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant/60"
                  >
                    <Bilingual {...copy.programModal.labelEndDate} />
                  </label>
                  <Input
                    id="program-end-date"
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="h-12 border-none bg-surface-container-low focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
            </section>
          </div>

          <footer className="flex shrink-0 items-center justify-end gap-4 bg-surface-container-low px-5 py-4 sm:px-8">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {t(copy.programModal.btnCancel)}
            </button>
            <Button
              type="submit"
              className="h-12 rounded-xl px-6 text-[10px] font-black uppercase tracking-[0.2em] shadow-level2"
              disabled={isPending}
            >
              {isPending ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              {t(copy.programModal.btnSubmit)}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
